import {
  ArrowRightIcon,
  BookOpenIcon,
  CheckIcon,
  DivideIcon,
  EqualIcon,
  MinusIcon,
  PercentIcon,
  PiIcon,
  PlayIcon,
  PlusIcon,
  QuoteIcon,
  RadicalIcon,
  SigmaIcon,
  XIcon,
} from "lucide-react";
import { type CSSProperties, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { HomepageSection } from "@/components/homepage/section";
import { HomepageScreenshot } from "@/components/homepage/screenshot";
import { HomepageSectionHeader } from "@/components/homepage/section-header";
import { BuyMeACoffeeIcon } from "@/components/icons/buy-me-a-coffee-icon";
import { RedditIcon } from "@/components/icons/reddit-icon";
import { XLogoIcon } from "@/components/icons/x-logo-icon";
import { useTheme } from "@/components/theme-provider";
import { Article } from "@/components/primitives/article";
import { CardLink } from "@/components/primitives/card-link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FAQ_SECTIONS } from "@/components/views/faq/faq-data";
import { FaqItems } from "@/components/views/faq/faq-items";
import { FAQPage, WithContext } from "schema-dts";
const TITLE = "Scoopulator: An approachable ice cream calculator";
const DESCRIPTION =
  "Leave the guesswork behind with the Scoopulator ice cream calculator. Balance your recipes, track every iteration, and share your best scoops.";
const CANONICAL_URL = "https://scoopulator.app/";
const IMAGE_URL =
  "https://scoopulator.app/screenshots/hero-calculator-light.webp";
type MathSymbolStyle = CSSProperties & {
  "--math-drift": string;
  "--math-symbol-fill": string;
  "--math-symbol-outline": string;
  "--math-rotation": string;
};
const MATH_SYMBOL_ICONS = [
  PlusIcon,
  DivideIcon,
  EqualIcon,
  XIcon,
  MinusIcon,
  PercentIcon,
  RadicalIcon,
  SigmaIcon,
  PiIcon,
] as const;
const MATH_SYMBOL_VARIANTS = [
  "blue",
  "cyan",
  "emerald",
  "orange",
  "pink",
  "rose",
  "violet",
  "amber",
  "fuchsia",
] as const;
const MATH_SYMBOL_SIZES = ["size-7", "size-8", "size-9"] as const;
interface MathSymbolDrop {
  id: number;
  Icon: (typeof MATH_SYMBOL_ICONS)[number];
  left: string;
  duration: string;
  delay: string;
  drift: string;
  rotation: string;
  variant: (typeof MATH_SYMBOL_VARIANTS)[number];
  size: (typeof MATH_SYMBOL_SIZES)[number];
  responsiveClassName?: string;
}
const INITIAL_MATH_SYMBOLS: MathSymbolDrop[] = [
  {
    id: 0,
    Icon: PlusIcon,
    left: "8%",
    duration: "13s",
    delay: "-2.6s",
    drift: "32px",
    rotation: "180deg",
    variant: "cyan",
    size: "size-8",
  },
  {
    id: 1,
    Icon: SigmaIcon,
    left: "28%",
    duration: "15s",
    delay: "-5.25s",
    drift: "-24px",
    rotation: "-210deg",
    variant: "violet",
    size: "size-9",
  },
  {
    id: 2,
    Icon: DivideIcon,
    left: "48%",
    duration: "12s",
    delay: "-5.4s",
    drift: "40px",
    rotation: "150deg",
    variant: "orange",
    size: "size-7",
  },
  {
    id: 3,
    Icon: RadicalIcon,
    left: "70%",
    duration: "14s",
    delay: "-7s",
    drift: "-36px",
    rotation: "-170deg",
    variant: "emerald",
    size: "size-9",
    responsiveClassName: "hidden md:block",
  },
  {
    id: 4,
    Icon: PiIcon,
    left: "90%",
    duration: "11s",
    delay: "-5.2s",
    drift: "20px",
    rotation: "220deg",
    variant: "rose",
    size: "size-8",
    responsiveClassName: "hidden lg:block",
  },
  {
    id: 5,
    Icon: EqualIcon,
    left: "18%",
    duration: "14s",
    delay: "-5.6s",
    drift: "-30px",
    rotation: "-140deg",
    variant: "blue",
    size: "size-7",
    responsiveClassName: "hidden lg:block",
  },
  {
    id: 6,
    Icon: PercentIcon,
    left: "58%",
    duration: "13s",
    delay: "-3.9s",
    drift: "28px",
    rotation: "200deg",
    variant: "amber",
    size: "size-8",
    responsiveClassName: "hidden xl:block",
  },
  {
    id: 7,
    Icon: XIcon,
    left: "82%",
    duration: "15s",
    delay: "-6s",
    drift: "-18px",
    rotation: "-190deg",
    variant: "fuchsia",
    size: "size-7",
    responsiveClassName: "hidden xl:block",
  },
];
function createPseudoRandom(seed: number) {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}
function randomBetween(random: () => number, min: number, max: number) {
  return min + random() * (max - min);
}
function getNextDropDelay(random: () => number) {
  const widthScale = 768 / Math.max(window.innerWidth, 320);
  return Math.max(180, randomBetween(random, 650, 1800) * widthScale);
}
function getNextDropLeft(random: () => number, previousLeft: number | null) {
  const minLeft = 2;
  const maxLeft = 98;
  const minimumGap = 22;
  if (previousLeft === null) return randomBetween(random, minLeft, maxLeft);
  const leftRangeLength = Math.max(0, previousLeft - minimumGap - minLeft);
  const rightRangeStart = previousLeft + minimumGap;
  const rightRangeLength = Math.max(0, maxLeft - rightRangeStart);
  const offset = random() * (leftRangeLength + rightRangeLength);
  return offset < leftRangeLength
    ? minLeft + offset
    : rightRangeStart + offset - leftRangeLength;
}
function MathSymbolShower() {
  const { resolvedTheme } = useTheme();
  const [symbols, setSymbols] =
    useState<MathSymbolDrop[]>(INITIAL_MATH_SYMBOLS);
  const fillShade = resolvedTheme === "dark" ? 800 : 200;
  const outlineShade = resolvedTheme === "dark" ? 950 : 300;
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const random = createPseudoRandom(Date.now() ^ performance.now());
    let nextId = INITIAL_MATH_SYMBOLS.length;
    let previousLeft: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout>;
    const dropSymbol = () => {
      const Icon =
        MATH_SYMBOL_ICONS[Math.floor(random() * MATH_SYMBOL_ICONS.length)];
      const variant =
        MATH_SYMBOL_VARIANTS[
          Math.floor(random() * MATH_SYMBOL_VARIANTS.length)
        ];
      const size =
        MATH_SYMBOL_SIZES[Math.floor(random() * MATH_SYMBOL_SIZES.length)];
      const left = getNextDropLeft(random, previousLeft);
      previousLeft = left;
      setSymbols((current) => [
        ...current,
        {
          id: nextId++,
          Icon,
          left: `${left.toFixed(2)}%`,
          duration: `${randomBetween(random, 10, 16).toFixed(2)}s`,
          delay: "0s",
          drift: `${randomBetween(random, -48, 48).toFixed(0)}px`,
          rotation: `${randomBetween(random, -240, 240).toFixed(0)}deg`,
          variant,
          size,
        },
      ]);
      timeoutId = setTimeout(dropSymbol, getNextDropDelay(random));
    };
    timeoutId = setTimeout(dropSymbol, randomBetween(random, 150, 900));
    return () => clearTimeout(timeoutId);
  }, []);
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {symbols.map(
        ({
          id,
          Icon,
          left,
          duration,
          delay,
          drift,
          rotation,
          variant,
          size,
          responsiveClassName,
        }) => {
          const style: MathSymbolStyle = {
            left,
            animationDuration: duration,
            animationDelay: delay,
            "--math-drift": drift,
            "--math-symbol-fill": `var(--color-${variant}-${fillShade})`,
            "--math-symbol-outline": `var(--color-${variant}-${outlineShade})`,
            "--math-rotation": rotation,
          };
          return (
            <span
              key={id}
              className={`hero-math-symbol absolute ${size} ${responsiveClassName ?? ""}`}
              onAnimationEnd={() => {
                setSymbols((current) =>
                  current.filter((symbol) => symbol.id !== id),
                );
              }}
              style={style}
            >
              <Icon
                className="absolute inset-0 size-full text-[var(--math-symbol-outline)]"
                strokeWidth={3}
              />
              <Icon className="absolute inset-0 size-full text-[var(--math-symbol-fill)]" />
            </span>
          );
        },
      )}
    </div>
  );
}
const FAQ_STRUCTURED_DATA: WithContext<FAQPage> = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({
      "@type": "Question" as const,
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer" as const,
        text: item.answer,
      },
    })),
  ),
};
export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Scoopulator" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL_URL },
      { property: "og:image", content: IMAGE_URL },
      {
        property: "og:image:alt",
        content:
          "Scoopulator ice cream calculator showing a recipe and composition targets",
      },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: IMAGE_URL },
      {
        name: "twitter:image:alt",
        content:
          "Scoopulator ice cream calculator showing a recipe and composition targets",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(FAQ_STRUCTURED_DATA).replace(/</g, "\\u003c"),
      },
    ],
  }),
  component: Home,
});
interface Testimonial {
  quote: string;
  user: string;
  initials: string;
  site: string;
  sourceUrl: string;
}
const FEATURED_TESTIMONIAL = {
  quote:
    "Going into Scoopulator and logging in, inputting two of my own ingredients and then developing that first recipe... it was super easy",
  user: "Polar Ice Creamery",
  channel: "YouTube",
  videoTitle: "A New Ice Cream Calculator? Is it any good!",
  videoUrl: "https://www.youtube.com/watch?v=prlcFm3nNwU",
  thumbnailUrl: "/screenshots/polar-ice-creamery-scoopulator-review.png",
  avatarUrl: "/avatars/polar-ice-creamery.webp",
};
const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "If you enjoy making your own ice cream, you've got to check out this brilliant web app!. It helps you make fantastic recipes, perfectly balanced with the right ingredients for your desired results.",
    user: "Daniel J. Lewis",
    initials: "DL",
    site: "X",
    sourceUrl: "https://x.com/theDanielJLewis/status/2017678279944102026",
  },
  {
    quote: "So much smoother than using a spreadsheet!",
    user: "Anonymous Supporter",
    initials: "AS",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
  {
    quote:
      "I would recommend putting your recipe into Scoopulator; it has upped my ice cream's texture in ways I couldn't have done alone.",
    user: "moistlittlefeeties",
    initials: "MF",
    site: "Reddit",
    sourceUrl:
      "https://www.reddit.com/r/icecreamery/comments/1szft88/comment/oj466z1/",
  },
  {
    quote: "Insanely good. Thanks for Scoopulator.",
    user: "man.of.rotee",
    initials: "mr",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
  {
    quote:
      "Use Scoopulator ice cream calculator. It has made me come up with my own recipes so much easier now. Truly a godsend.",
    user: "Beautiful_Rabbit_925",
    initials: "BR",
    site: "Reddit",
    sourceUrl:
      "https://www.reddit.com/r/icecreamery/comments/1pwpccm/comment/nw5s696/",
  },
  {
    quote:
      "Several recent highly successful ice creams … the app is quite useful in editing recipes in order to make them more scoopable.",
    user: "Chris E.",
    initials: "CE",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
  {
    quote: "For beginners, I do recommend Scoopulator.",
    user: "jhermann",
    initials: "JH",
    site: "GitHub Pages",
    sourceUrl:
      "https://jhermann.github.io/ice-creamery/info/web-resources/?h=ice+cream+calculators#ice-cream-calculators",
  },
  {
    quote: "Great app, it has made my ice cream making so much easier! Thanks!",
    user: "jeff",
    initials: "J",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
  {
    quote:
      "This is great, it will save me a lot of time searching for moisture content mostly which is often the hardest thing to find.",
    user: "Benjamin M.",
    initials: "BM",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
  {
    quote:
      "This is super easy to use and understand. I find it easier than the spreadsheet that floats around as well.",
    user: "wickedfalina",
    initials: "WF",
    site: "Reddit",
    sourceUrl:
      "https://www.reddit.com/r/icecreamery/comments/1loa57h/im_building_a_simple_to_use_webbased_ice_cream/",
  },
  {
    quote:
      "Really love the tool, been very helpful. Thanks for making and maintaining it!",
    user: "Anonymous Supporter",
    initials: "AS",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
  {
    quote:
      "Just wanted to thank you for this, as a newbie I'm able to dive in a bit more easily into Ice Cream, with a beautiful UI!",
    user: "lilpiglet",
    initials: "LP",
    site: "Reddit",
    sourceUrl:
      "https://www.reddit.com/r/icecreamery/comments/1loa57h/comment/o6ki1rl/",
  },
  {
    quote: "Thank you for scoopulator, it's great!",
    user: "Gé van Vliet",
    initials: "GV",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
  {
    quote:
      "Try using the Scoopulator ice cream calculator. It’s really not hard to use and I’ve found it enlightening. Way better than trial and error-ing your way to greater success.",
    user: "jeffmiho",
    initials: "JM",
    site: "Reddit",
    sourceUrl:
      "https://www.reddit.com/r/icecreamery/comments/1ulzjvr/comment/ovbi19n/",
  },
  {
    quote: "Me and my family really appreciate your site!",
    user: "Avi",
    initials: "A",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
  {
    quote: "Thanks for the great calculator, it's is fantastic.",
    user: "Tim",
    initials: "T",
    site: "Buy Me a Coffee",
    sourceUrl: "https://www.buymeacoffee.com/scoopulator",
  },
];
const TESTIMONIAL_ROWS = [
  TESTIMONIALS.filter((_, index) => index % 2 === 0),
  TESTIMONIALS.filter((_, index) => index % 2 === 1),
];
function TestimonialCard({
  testimonial,
  isDuplicate = false,
}: {
  testimonial: Testimonial;
  isDuplicate?: boolean;
}) {
  return (
    <CardLink
      href={testimonial.sourceUrl}
      external
      target="_blank"
      rel="noreferrer"
      tabIndex={isDuplicate ? -1 : undefined}
      srLabel={`Read testimonial by ${testimonial.user} on ${testimonial.site}`}
      className="testimonial-card w-[clamp(18rem,25vw,24rem)] shrink-0 p-5 md:p-6"
    >
      <div className="flex items-center gap-2.5">
        <div
          aria-hidden="true"
          className="text-muted-foreground flex size-5 shrink-0 items-center justify-center text-xs font-bold"
        >
          {testimonial.site === "Buy Me a Coffee" ? (
            <BuyMeACoffeeIcon aria-hidden="true" className="h-5 w-auto" />
          ) : testimonial.site === "Reddit" ? (
            <RedditIcon aria-hidden="true" className="size-5 fill-[#ff4500]" />
          ) : testimonial.site === "X" ? (
            <XLogoIcon
              aria-hidden="true"
              className="size-5 fill-black dark:fill-white"
            />
          ) : (
            testimonial.initials
          )}
        </div>
        <span className="inline-flex min-w-0 items-center gap-1 text-sm font-semibold group-hover:underline">
          <span className="truncate">{testimonial.user}</span>
        </span>
      </div>
      <blockquote className="mt-3">
        <p className="leading-relaxed text-pretty whitespace-pre-line">
          “{testimonial.quote}”
        </p>
      </blockquote>
    </CardLink>
  );
}
function TestimonialMarqueeRow({
  testimonials,
  direction,
}: {
  testimonials: Testimonial[];
  direction: "left" | "right";
}) {
  return (
    <div className="testimonial-marquee-row">
      <div
        className="testimonial-marquee-track flex w-max"
        data-direction={direction}
      >
        {[false, true].map((isDuplicate) => (
          <div
            key={String(isDuplicate)}
            aria-hidden={isDuplicate || undefined}
            className="flex shrink-0 gap-4 pr-4"
          >
            {testimonials.map((testimonial) => (
              <TestimonialCard
                key={testimonial.user}
                testimonial={testimonial}
                isDuplicate={isDuplicate}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
function FeaturedTestimonialCard() {
  return (
    <Card className="mx-auto mb-16 w-full max-w-xl overflow-hidden">
      <div>
        <a
          href={FEATURED_TESTIMONIAL.videoUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Watch ${FEATURED_TESTIMONIAL.videoTitle} on YouTube`}
          className="group focus-visible:ring-ring relative flex aspect-video items-center justify-center overflow-hidden bg-[hsl(var(--gray-950))] text-[hsl(var(--gray-50))] focus-visible:ring-2 focus-visible:outline-hidden"
        >
          <img
            src={FEATURED_TESTIMONIAL.thumbnailUrl}
            alt=""
            width={720}
            height={404}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 size-full object-cover"
          />
          <span className="relative flex size-16 items-center justify-center rounded-full bg-[hsl(var(--red-500))] shadow-2xl transition-transform duration-200 group-hover:scale-105 motion-reduce:transition-none md:size-20">
            <PlayIcon aria-hidden="true" className="ml-1 size-8 fill-current" />
          </span>
        </a>

        <div className="p-4 md:p-6">
          <div className="flex items-center">
            <QuoteIcon
              aria-hidden="true"
              className="text-primary size-6 fill-current"
            />
          </div>
          <blockquote className="mt-4">
            <p className="text-xl leading-snug font-medium tracking-tight text-pretty md:text-2xl">
              “{FEATURED_TESTIMONIAL.quote}”
            </p>
          </blockquote>
        </div>
        <div className="flex items-center justify-between p-4 pt-0 md:p-6 md:pt-0">
          <div className="flex items-center gap-3">
            <img
              src={FEATURED_TESTIMONIAL.avatarUrl}
              alt=""
              width={48}
              height={48}
              loading="lazy"
              decoding="async"
              className="rounded-full object-cover"
            />
            <div>
              <p className="font-semibold">{FEATURED_TESTIMONIAL.user}</p>
              <p className="text-muted-foreground text-sm">
                {FEATURED_TESTIMONIAL.channel}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
// function ResponsiveDeviceShowcase() {
//   return (
//     <HomepageSection className="border-b px-4">
//       <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
//         <HomepageSectionHeader
//           label="Wherever you are"
//           title="Looks great on any device"
//           description="Start a recipe at your desk, make adjustments from the kitchen, and check your notes beside the churn. Scoopulator stays clear and easy to use on every screen."
//         >
//           <div className="mt-7 flex flex-wrap gap-3">
//             <span className="bg-background border-border/80 inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium shadow-xs">
//               <MonitorIcon aria-hidden="true" className="text-primary size-4" />
//               Desktop
//             </span>
//             <span className="bg-background border-border/80 inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium shadow-xs">
//               <SmartphoneIcon
//                 aria-hidden="true"
//                 className="text-primary size-4"
//               />
//               Phone &amp; tablet
//             </span>
//           </div>
//           <Button render={<Link to="/calc" />} variant="outline" className="bg-card mt-8">
//               Try it on this device
//               <ArrowRightIcon aria-hidden="true" />
//           </Button>
//         </HomepageSectionHeader>
//         <div className="relative mx-auto w-full max-w-3xl pt-4 pb-12 sm:px-8 sm:pt-8 sm:pb-16">
//           <div
//             aria-hidden="true"
//             className="bg-primary/20 absolute inset-x-16 bottom-4 h-24 rounded-full blur-3xl"
//           />
//           <HomepageScreenshot
//             src="/screenshots/calculator.webp"
//             alt="Scoopulator calculator displayed on a desktop screen"
//             width={1624}
//             height={1168}
//             chromeLabel="scoopulator.app"
//             frameClassName="ml-auto w-[92%] shadow-[0_24px_65px_-30px_hsl(var(--foreground)/0.5)] sm:w-[88%] sm:rounded-[1.5rem] sm:p-2.5"
//             chromeClassName="h-8 px-3 [&>span:last-child]:ml-2 [&>span:last-child]:text-[10px] [&>span:not(:last-child)]:size-2"
//             imageClassName="border-border/70 aspect-[1.55/1] rounded-t-none rounded-b-xl object-cover object-top"
//           />
//           <HomepageScreenshot
//             src="/screenshots/calculator.webp"
//             alt="Scoopulator calculator displayed on a phone"
//             width={1624}
//             height={1168}
//             frameClassName="border-foreground/80 bg-background absolute bottom-0 left-0 w-[30%] min-w-32 rounded-[1.75rem] border-[5px] p-1.5 shadow-[0_24px_55px_-24px_hsl(var(--foreground)/0.65)] sm:left-2 sm:w-[27%] sm:rounded-[2.25rem] sm:border-[7px] sm:p-2"
//             imageClassName="border-border/60 aspect-[9/17] rounded-[1.25rem] object-cover object-left sm:rounded-[1.65rem]"
//             overlay={
//               <div
//                 aria-hidden="true"
//                 className="bg-foreground absolute top-1 left-1/2 z-10 h-2 w-10 -translate-x-1/2 rounded-full sm:top-1.5 sm:h-2.5 sm:w-14"
//               />
//             }
//           />
//           <div className="bg-card border-border/80 absolute right-0 bottom-3 hidden items-center gap-2 rounded-full border px-3 py-2 text-xs font-medium shadow-lg sm:flex">
//             <span className="bg-green-bg text-green-foreground flex size-6 items-center justify-center rounded-full">
//               <CheckIcon aria-hidden="true" className="size-3.5" />
//             </span>
//             Responsive by design
//           </div>
//         </div>
//       </div>
//     </HomepageSection>
//   );
// }
function Home() {
  return (
    <div className="overflow-hidden">
      <section className="relative isolate overflow-hidden px-4 pt-12 pb-16 sm:pt-16 lg:pt-20 lg:pb-24">
        <MathSymbolShower />
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
          <div className="relative z-10 mx-auto max-w-2xl text-center lg:mx-0 lg:text-left">
            <h1 className="text-4xl font-bold tracking-tight text-balance sm:text-6xl lg:text-6xl">
              An ice cream calculator you'll actually enjoy using
            </h1>
            <p className="text-muted-foreground mx-auto mt-6 max-w-xl text-lg leading-relaxed text-pretty sm:text-xl lg:mx-0">
              Leave the guesswork behind. Balance your recipes, track every
              iteration, and share your best scoops.
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
              <Button size="lg" render={<Link to="/calc" />}>
                Open the calculator
                <ArrowRightIcon aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                render={<Link to="/discover" />}
              >
                <BookOpenIcon aria-hidden="true" />
                Browse community recipes
              </Button>
            </div>

            <div className="text-muted-foreground mt-7 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm lg:justify-start">
              <span className="flex items-center gap-1.5">
                <CheckIcon aria-hidden="true" className="text-primary size-4" />
                Free to use
              </span>
              <span className="flex items-center gap-1.5">
                <CheckIcon aria-hidden="true" className="text-primary size-4" />
                No account required
              </span>
              <span className="flex items-center gap-1.5">
                <CheckIcon aria-hidden="true" className="text-primary size-4" />
                Built for any machine
              </span>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-3xl lg:mr-[-8%]">
            <HomepageScreenshot
              src="/screenshots/hero-calculator-tall-light.webp"
              darkSrc="/screenshots/hero-calculator-tall-dark.webp"
              alt="Screenshot of the Scoopulator ice cream calculator"
              width={1200}
              height={844}
              loading="eager"
              fetchPriority="high"
              chromeLabel="scoopulator.app/calc"
              imageClassName="aspect-[1200/844] object-cover object-top"
            />
          </div>
        </div>
      </section>

      <HomepageSection background="muted">
        <div className="px-4">
          <HomepageSectionHeader
            className="mx-auto mb-12 max-w-2xl text-center md:mb-16"
            title="Loved by ice cream enthusiasts"
            description="See what users are saying about Scoopulator."
          />

          <FeaturedTestimonialCard />
        </div>

        <div className="testimonial-marquee space-y-4">
          <TestimonialMarqueeRow
            testimonials={TESTIMONIAL_ROWS[0]}
            direction="left"
          />
          <TestimonialMarqueeRow
            testimonials={TESTIMONIAL_ROWS[1]}
            direction="right"
          />
        </div>
      </HomepageSection>
      {/*
        <FeatureDeepDive
          label="Freezing analysis"
          title="Know how it scoops before it freezes"
          description="A recipe can look balanced on paper and still freeze rock-hard. Scoopulator maps frozen water across temperature, so you can formulate for the texture and serving style you actually want."
          features={[
            "See ideal serving temperature at a glance",
            "Account for hard mix-ins such as nuts and chocolate",
            "Tune recipes for scoopable, gelato, or soft-serve textures",
          ]}
          ctaText="Explore freezing analysis"
          ctaHref="/calc"
          screenshots={[
            {
              alt: "Freezing curve showing Scoopulator's ideal scoopability range",
              src: "/screenshots/freezing-curve.webp",
              width: 1018,
              height: 591,
            },
            {
              alt: "Freezing gauge showing serving temperature feedback",
              src: "/screenshots/freezing-gauge.webp",
              width: 900,
              height: 454,
            },
          ]}
        />

        <FeatureDeepDive
          label="Your recipe library"
          title="Keep every good batch—and learn from the rest"
          description="Save your formulas, record what happened, and refine each version over time. Your recipe library becomes a practical notebook for everything you churn."
          features={[
            "Save unlimited recipes for free",
            "Record notes and full nutritional information",
            "Keep recipes private or share them when they are ready",
          ]}
          ctaText="View your recipes"
          ctaHref="/recipes"
          screenshots={[
            {
              alt: "A Scoopulator recipe library with saved ice cream recipes",
              src: "/screenshots/recipe-list.webp",
              width: 1112,
              height: 985,
            },
          ]}
          imagePosition="left"
          variant="muted"
        />

        <ResponsiveDeviceShowcase /> */}

      <HomepageSection className="px-4">
        <div className="mx-auto max-w-3xl">
          <HomepageSectionHeader
            className="mx-auto mb-12 max-w-2xl text-center"
            title="Frequently asked questions"
            description="Find answers to common questions about Scoopulator."
          />

          <Article>
            <FaqItems />
          </Article>

          <div className="mt-10 flex justify-center">
            <Button
              variant="outline"
              className="bg-card"
              render={<Link to="/learn/faq" />}
            >
              Browse all FAQs
              <ArrowRightIcon aria-hidden="true" />
            </Button>
          </div>
        </div>
      </HomepageSection>
    </div>
  );
}
